import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { keepWithinSource } from "../library/keepWithinSource.js";
import { SHELF_IDS, type ShelfId } from "@hybrid-iptv/tmdb-client";
import { api, runsOnDevice } from "../api/client.js";
import { nativeCatalogAvailable, nativeDiscoveryRefresh } from "../api/nativeCatalog.js";
import type { DiscoveryEntry, DiscoveryRowsResult } from "../api/nativeCatalog.js";
import { providerBody } from "../epg/api.js";
import { useLockedCategoryIds } from "../library/useCatalogQuery.js";
import { useVodCatalog } from "../library/vodHooks.js";
import { useDiscoveryIndex } from "../discovery/discoveryFiles.js";
import { webDiscoveryRows } from "../discovery/webDiscovery.js";
import { displayTitle } from "./shelfFill.js";
import type { OpenRowEntry, OpenRowLists } from "./openRows.js";

/**
 * Home's rows from the discovery files (Section 26 step 4; ADR "Home rows and title pages
 * from open data"): Trending and Popular from Wikipedia's views, the genre rows from
 * Wikidata, all over titles the viewer's catalog has, one group a day, no title twice. The
 * device answers (CatalogStore.discoveryRows); the page gets about a hundred titles, never the
 * metadata. With no discovery file yet the answer is null and Home keeps its catalog-only
 * rows (openRows.ts), so a first launch is never empty.
 */

/** How often the device asks the server whether a discovery file is newer (it answers 304 when not). */
const REFRESH_MS = 3 * 60 * 60 * 1000;

export interface OpenDiscovery {
  lists: OpenRowLists;
  byShelf: Map<ShelfId, OpenRowEntry[]>;
  spotlight: { type: string; title: string; items: OpenRowEntry[] } | null;
}

/** A discovery entry in Home's list shape: the discovery file's id, name and year; the provider's pictures. */
export function discoveryEntry(entry: DiscoveryEntry): OpenRowEntry | null {
  const item = entry.item as { rawTitle: string; posterUrl?: string; backdropUrl?: string; rating?: number; year?: number };
  if (!item.posterUrl) return null;
  const year = entry.year ?? item.year;
  return {
    id: entry.tmdb,
    title: entry.label ?? displayTitle(item.rawTitle),
    posterUrl: item.posterUrl,
    ...(item.backdropUrl ? { backdropUrl: item.backdropUrl } : {}),
    ...(item.rating !== undefined ? { vote_average: item.rating } : {}),
    ...(year ? { release_date: String(year) } : {}),
  };
}

const entries = (list: readonly DiscoveryEntry[] | undefined): OpenRowEntry[] =>
  (list ?? []).map(discoveryEntry).filter((entry): entry is OpenRowEntry => entry !== null);

export function openDiscovery(result: DiscoveryRowsResult): OpenDiscovery {
  return {
    lists: { trending: entries(result.rows.trending), popular: entries(result.rows.popular) },
    byShelf: new Map(SHELF_IDS.map((shelf) => [shelf, entries(result.rows[shelf])])),
    spotlight: result.spotlight ? { type: result.spotlight.type, title: result.spotlight.title, items: entries(result.spotlight.items) } : null,
  };
}

/**
 * The device's discovery files: asked about once a launch and every few hours after. `stamp`
 * changes only when a new file was swapped in, so the rows are asked for again only then.
 */
function useDiscoveryFiles(enabled: boolean) {
  const files = useQuery({
    queryKey: ["discoveryFiles"],
    queryFn: () => nativeDiscoveryRefresh(),
    enabled: enabled && nativeCatalogAvailable(),
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  return files.data?.changed ? files.dataUpdatedAt : 0;
}

/**
 * One side's rows (films or series) for the day. `undefined` while asking; `null` when there
 * are no discovery files yet. On the device the native catalog answers; on the website the page
 * does, over the catalog it holds and the public files (discovery/webDiscovery.ts).
 */
export function useDiscoveryRows(
  sourceId: string,
  media: "movie" | "tv",
  day: number,
  size: number,
  kids: boolean,
  enabled: boolean,
): { discovery: OpenDiscovery | null | undefined; isLoading: boolean } {
  const kind = media === "tv" ? "series" : "vod";
  const device = runsOnDevice() && nativeCatalogAvailable();
  const stamp = useDiscoveryFiles(enabled && device);
  const locked = useLockedCategoryIds(sourceId, kind, enabled && device);
  const web = !runsOnDevice();
  const catalog = useVodCatalog(sourceId, enabled && web && Boolean(sourceId));
  const index = useDiscoveryIndex(enabled && web);
  const items = kind === "series" ? catalog.data?.series : catalog.data?.vod;
  const webRows = useMemo(
    () => (index && items ? webDiscoveryRows(index, items, { kind, day, size, kids }) : null),
    [index, items, kind, day, size, kids],
  );
  const query = useQuery({
    queryKey: ["discoveryRows", sourceId, kind, day, size, kids, locked, stamp],
    queryFn: () =>
      api.post<DiscoveryRowsResult | null>(`/api/sources/${encodeURIComponent(sourceId)}/discovery/rows`, {
        ...providerBody(),
        request: { kind, day, size, kids },
        excludeCategories: locked,
      }),
    enabled: enabled && device && Boolean(sourceId) && locked !== undefined,
    staleTime: Infinity,
    placeholderData: keepWithinSource(sourceId),
    retry: false,
  });
  const discovery = useMemo(() => {
    if (!enabled) return null;
    if (web) {
      // The catalog or the files still loading is "asking"; no files at all is "none".
      if (index === null || catalog.isError) return null;
      if (index === undefined || items === undefined) return undefined;
      return webRows ? openDiscovery(webRows) : null;
    }
    if (!device) return null;
    if (query.isError) return null;
    if (query.data === undefined) return undefined;
    return query.data ? openDiscovery(query.data) : null;
  }, [enabled, device, web, index, items, catalog.isError, webRows, query.isError, query.data]);
  return { discovery, isLoading: discovery === undefined };
}
